import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Palette, Loader2, UserPlus, X, Save, Info, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCreativeTeamTab() {
  const [members, setMembers] = useState([]); // current configured
  const [allUsers, setAllUsers] = useState([]);
  const [draftIds, setDraftIds] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
  const h = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = async () => {
    setLoading(true);
    try {
      const [tmRes, usersRes] = await Promise.all([
        axios.get(`${API}/creative-scorecard/team-members`, h),
        axios.get(`${API}/admin/users`, h),
      ]);
      const users = (usersRes.data?.users || usersRes.data || []).filter((u) => u.status === 'active');
      setAllUsers(users);
      setMembers(tmRes.data?.members || []);
      setDraftIds([...(tmRes.data?.member_ids || [])]);
    } catch (e) {
      toast.error('Failed to load creative team data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/creative-scorecard/team-members`, { member_ids: draftIds }, h);
      toast.success(`Creative team saved (${draftIds.length} members)`);
      await load();
    } catch (e) {
      toast.error('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const toggle = (uid) => {
    setDraftIds((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
  };

  const filteredUsers = allUsers.filter((u) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (u.name || '').toLowerCase().includes(s) || (u.email || '').toLowerCase().includes(s) || (u.department || '').toLowerCase().includes(s);
  });

  const isDirty = useMemo(() => {
    const currentIds = (members || []).map((m) => m.id).sort();
    const draft = [...draftIds].sort();
    return JSON.stringify(currentIds) !== JSON.stringify(draft);
  }, [draftIds, members]);

  if (loading) {
    return <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin text-pink-400" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-creative-team-tab">
      {/* Header card */}
      <div className="bg-gradient-to-r from-pink-500/10 to-rose-500/5 border border-pink-500/20 rounded-xl p-4 flex items-start gap-3">
        <Palette className="text-pink-400 flex-shrink-0 mt-0.5" size={18} />
        <div className="flex-1">
          <h3 className="text-white font-semibold mb-1">Creative Team Members</h3>
          <p className="text-xs text-[hsl(var(--text-muted))]">
            Pilih 4 ahli creative (editor, shooter, designer) yang akan share semua "Creative" scorecard nodes.
            Ahli yang dipilih akan auto-bypass standard 1-to-1 user-node mapping dan automatik nampak Creative View dalam My Scorecard mereka.
            Individual target = team_total_target ÷ jumlah ahli.
          </p>
        </div>
      </div>

      {/* Current selection summary */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <CheckCircle2 size={14} className="text-emerald-400" />
            Selected ({draftIds.length})
          </h4>
          {isDirty && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setDraftIds([...(members || []).map((m) => m.id)])} className="border-[hsl(var(--border))]" data-testid="admin-creative-team-cancel">
                <X size={12} className="mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-pink-500 to-rose-600 text-white" data-testid="admin-creative-team-save">
                {saving ? <Loader2 size={12} className="animate-spin mr-1" /> : <Save size={12} className="mr-1" />} Save
              </Button>
            </div>
          )}
        </div>
        {draftIds.length === 0 ? (
          <p className="text-xs text-[hsl(var(--text-muted))] italic py-2">No members selected yet. Pick users from the list below.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {draftIds.map((uid) => {
              const u = allUsers.find((x) => x.id === uid);
              return (
                <Badge key={uid} className="bg-pink-500/10 text-pink-400 border border-pink-500/20 text-xs py-1 px-2 flex items-center gap-1.5" data-testid={`admin-creative-selected-${uid}`}>
                  {u?.name || uid.slice(0, 8)}
                  <button onClick={() => toggle(uid)} className="hover:text-white"><X size={10} /></button>
                </Badge>
              );
            })}
          </div>
        )}
      </div>

      {/* User picker */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <UserPlus size={14} className="text-amber-400" />
            All Users (click to toggle)
          </h4>
          <Input
            placeholder="Search by name, email, department..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] h-8 text-xs"
            data-testid="admin-creative-search"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 max-h-[480px] overflow-y-auto pr-1">
          {filteredUsers.map((u) => {
            const picked = draftIds.includes(u.id);
            return (
              <button
                key={u.id}
                onClick={() => toggle(u.id)}
                data-testid={`admin-creative-user-${u.id}`}
                className={`text-left rounded-lg border p-2.5 transition flex items-center gap-2.5 ${
                  picked
                    ? 'bg-pink-500/10 border-pink-500/40 ring-1 ring-pink-500/30'
                    : 'bg-[hsl(var(--surface-elevated))]/40 border-[hsl(var(--border))] hover:border-[hsl(var(--border-hover))]'
                }`}
              >
                {u.avatar_url ? (
                  <img src={u.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-white text-xs font-bold">
                    {(u.name || '?').slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{u.name}</div>
                  <div className="text-[10px] text-[hsl(var(--text-muted))] truncate">
                    {u.department || '—'}{u.position ? ` · ${u.position}` : ''}
                  </div>
                </div>
                {picked && <CheckCircle2 size={14} className="text-pink-400 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
        {filteredUsers.length === 0 && (
          <p className="text-xs text-[hsl(var(--text-muted))] py-4 text-center">No users match search.</p>
        )}
      </div>

      {/* Footer notes */}
      <div className="text-[11px] text-[hsl(var(--text-muted))] bg-[hsl(var(--surface-elevated))]/40 border border-[hsl(var(--border))] rounded-lg p-3 flex items-start gap-2">
        <Info size={12} className="flex-shrink-0 mt-0.5" />
        <div>
          <strong>How it works:</strong> Targets (poster, video ads, video organic) are still managed under
          <strong> Admin → Scorecard → Targets</strong> per "Creative" STAFF node.
          The members selected here share visibility across all those nodes via the Creative View in <strong>My Scorecard</strong>.
        </div>
      </div>
    </div>
  );
}
